import React, { useState } from 'react';
import {
  CheckCircle2,
  XCircle,
  RefreshCw,
  Activity,
  Hand,
  Eye,
  Smile,
  Zap,
  Smartphone,
  MessageSquare,
  ChevronDown,
  ChevronUp,
  Clock
} from 'lucide-react';

const ReviewGate = ({ execution, onApprove, onReject, onRegenerate, loading }) => {
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  const handleReject = () => {
    if (rejectReason.trim()) {
      onReject(rejectReason);
      setShowRejectModal(false);
      setRejectReason('');
    }
  };

  if (execution.status === 'approved') {
    return (
      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <div className="flex items-center gap-2 text-green-700">
          <CheckCircle2 className="w-5 h-5" />
          <span className="font-medium">Execution approvata</span>
        </div>
      </div>
    );
  }

  if (execution.status === 'rejected') {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-center gap-2 text-red-700">
          <XCircle className="w-5 h-5" />
          <span className="font-medium">Execution rigettata</span>
        </div>
        {execution.rejectionReason && (
          <p className="text-sm text-red-600 mt-2">{execution.rejectionReason}</p>
        )}
      </div>
    );
  }

  return (
    <>
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <h3 className="font-medium text-gray-800 mb-3">Revisiona Output</h3>
        <div className="flex gap-3">
          <button onClick={() => onApprove()} disabled={loading}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50">
            <CheckCircle2 className="w-4 h-4" /> Approva
          </button>
          <button onClick={onRegenerate} disabled={loading}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50">
            <RefreshCw className="w-4 h-4" /> Rigenera
          </button>
          <button onClick={() => setShowRejectModal(true)} disabled={loading}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50">
            <XCircle className="w-4 h-4" /> Rigetta
          </button>
        </div>
      </div>

      {showRejectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Motivo del Rigetto</h3>
            <textarea value={rejectReason} onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Spiega perché rigetti questa execution..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              rows={4} />
            <div className="flex gap-3 mt-4">
              <button onClick={() => setShowRejectModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
                Annulla
              </button>
              <button onClick={handleReject} disabled={!rejectReason.trim()}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50">
                Conferma Rigetto
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const MotionHeader = ({ profile = {} }) => (
  <div className="bg-violet-50 border border-violet-200 rounded-lg p-4 space-y-3">
    {profile.posture_baseline && (
      <div>
        <p className="text-xs font-medium text-violet-500 uppercase tracking-wide mb-1">Posture Baseline</p>
        <p className="text-sm font-semibold text-violet-800 leading-relaxed">{profile.posture_baseline}</p>
      </div>
    )}
    {profile.body_motion_style && (
      <div>
        <p className="text-xs font-medium text-violet-500 uppercase tracking-wide mb-1">Body Motion Style</p>
        <p className="text-sm text-violet-700 leading-relaxed">{profile.body_motion_style}</p>
      </div>
    )}
  </div>
);

const GestureProfileCard = ({ gestureProfile = {} }) => {
  const [expanded, setExpanded] = useState(true);
  if (!gestureProfile || !Object.keys(gestureProfile).length) return null;

  return (
    <div className="rounded-lg border border-amber-200 overflow-hidden">
      <button onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-3 bg-amber-50 hover:bg-amber-100 transition-colors">
        <div className="flex items-center gap-2">
          <Hand className="w-4 h-4 text-amber-600" />
          <span className="text-xs font-semibold uppercase tracking-wide text-amber-700">Gesture Profile</span>
        </div>
        {expanded
          ? <ChevronUp className="w-4 h-4 text-amber-500" />
          : <ChevronDown className="w-4 h-4 text-amber-500" />}
      </button>
      {expanded && (
        <div className="bg-white p-4 space-y-2 border-t border-amber-100">
          {Object.entries(gestureProfile).map(([key, value]) => (
            <div key={key}>
              <span className="text-xs font-medium text-gray-500 capitalize">{key.replace(/_/g, ' ')}: </span>
              <span className="text-sm text-gray-800">{String(value)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const SpeechBehaviorCard = ({ sbp = {} }) => {
  const [expanded, setExpanded] = useState(true);
  const hasContent = Object.values(sbp).some(v => v && (typeof v === 'string' ? v.length : v.length > 0));
  if (!hasContent) return null;

  const stringFields = ['thought_to_speech_style', 'spontaneity_level'];
  const arrayFields = [
    { key: 'cognitive_search_patterns', label: 'Cognitive Search Patterns' },
    { key: 'hesitation_rules', label: 'Hesitation Rules' },
    { key: 'self_interruption_rules', label: 'Self-Interruption Rules' },
    { key: 'attention_shift_rules', label: 'Attention Shift Rules' },
    { key: 'memory_retrieval_signals', label: 'Memory Retrieval Signals' },
    { key: 'performance_suppression_rules', label: 'Performance Suppression Rules' },
  ];

  return (
    <div className="rounded-lg border border-teal-200 overflow-hidden">
      <button onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-3 bg-teal-50 hover:opacity-90 transition-opacity">
        <div className="flex items-center gap-2">
          <MessageSquare className="w-4 h-4 text-teal-700" />
          <span className="text-xs font-semibold uppercase tracking-wide text-teal-700">Speech Behavior Profile</span>
        </div>
        {expanded
          ? <ChevronUp className="w-4 h-4 text-teal-500 opacity-50" />
          : <ChevronDown className="w-4 h-4 text-teal-500 opacity-50" />}
      </button>
      {expanded && (
        <div className="bg-white p-4 space-y-4 border-t border-gray-100">
          {stringFields.map(field => sbp[field] ? (
            <div key={field}>
              <p className="text-xs font-medium text-gray-500 capitalize">{field.replace(/_/g, ' ')}</p>
              <p className="text-sm text-gray-800 mt-0.5">{sbp[field]}</p>
            </div>
          ) : null)}
          {arrayFields.map(({ key, label }) => Array.isArray(sbp[key]) && sbp[key].length > 0 ? (
            <div key={key}>
              <p className="text-xs font-medium text-gray-500 mb-1">{label}</p>
              <ul className="space-y-1">
                {sbp[key].map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                    <span className="text-teal-500 mt-0.5 font-bold">·</span>
                    {typeof item === 'string' ? item : JSON.stringify(item)}
                  </li>
                ))}
              </ul>
            </div>
          ) : null)}
        </div>
      )}
    </div>
  );
};

const RulesList = ({ icon: Icon, title, items = [], colorClass, bgClass, borderClass }) => {
  const [expanded, setExpanded] = useState(true);
  if (!items || !items.length) return null;

  return (
    <div className={`rounded-lg border ${borderClass} overflow-hidden`}>
      <button onClick={() => setExpanded(!expanded)}
        className={`w-full flex items-center justify-between p-3 ${bgClass} hover:opacity-90 transition-opacity`}>
        <div className="flex items-center gap-2">
          <Icon className={`w-4 h-4 ${colorClass}`} />
          <span className={`text-xs font-semibold uppercase tracking-wide ${colorClass}`}>{title}</span>
          <span className="ml-auto text-xs text-gray-400">{items.length} rules</span>
        </div>
        {expanded
          ? <ChevronUp className={`w-4 h-4 ${colorClass} opacity-50`} />
          : <ChevronDown className={`w-4 h-4 ${colorClass} opacity-50`} />}
      </button>
      {expanded && (
        <ul className="divide-y divide-gray-100 bg-white border-t border-gray-100">
          {items.map((item, i) => (
            <li key={i} className="flex items-start gap-3 p-3">
              <span className={`w-5 h-5 flex-shrink-0 flex items-center justify-center rounded-full text-xs font-bold ${colorClass} bg-gray-50 border border-current mt-0.5`}>
                {i + 1}
              </span>
              <p className="text-sm text-gray-700 leading-relaxed">{typeof item === 'string' ? item : JSON.stringify(item)}</p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default function Contract14BView({ execution, onApprove, onReject, onRegenerate, loading }) {
  if (!execution) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center text-gray-500">
          <Clock className="w-12 h-12 mx-auto mb-4 text-gray-300" />
          <p className="text-lg font-medium mb-2">In attesa di esecuzione</p>
          <p className="text-sm">Clicca "Esegui Contract" per avviare il Human Motion Compiler</p>
        </div>
      </div>
    );
  }

  const output = execution.output || execution.outputData || {};
  const {
    human_motion_profile = {},
    gesture_rules = [],
    instability_rules = [],
    smartphone_motion_rules = []
  } = output;

  return (
    <div className="h-full overflow-y-auto p-6">
      <div className="max-w-4xl mx-auto space-y-6">

        <div>
          <div className="flex items-center gap-3 mb-1">
            <Activity className="w-6 h-6 text-violet-600" />
            <h2 className="text-xl font-bold text-gray-800">Human Motion Compiler</h2>
            <span className="px-2 py-0.5 text-xs bg-violet-100 text-violet-700 rounded-full font-medium">C14B</span>
          </div>
          <p className="text-sm text-gray-500">
            Compilazione del modello comportamentale latente in specifica motoria osservabile
          </p>
        </div>

        <MotionHeader profile={human_motion_profile} />

        <div className="space-y-4">
          <RulesList icon={Eye} title="Eye Motion Rules" items={human_motion_profile.eye_motion_rules}
            colorClass="text-blue-700" bgClass="bg-blue-50" borderClass="border-blue-200" />
          <RulesList icon={Activity} title="Head Motion Rules" items={human_motion_profile.head_motion_rules}
            colorClass="text-violet-700" bgClass="bg-violet-50" borderClass="border-violet-200" />
          <RulesList icon={Smile} title="Facial Dynamics Rules" items={human_motion_profile.facial_dynamics_rules}
            colorClass="text-pink-700" bgClass="bg-pink-50" borderClass="border-pink-200" />
          <GestureProfileCard gestureProfile={human_motion_profile.gesture_profile} />
          <RulesList icon={Hand} title="Hand Motion Rules" items={human_motion_profile.hand_motion_rules}
            colorClass="text-amber-700" bgClass="bg-amber-50" borderClass="border-amber-200" />
          <RulesList icon={Activity} title="Idle Motion Rules" items={human_motion_profile.idle_motion_rules}
            colorClass="text-green-700" bgClass="bg-green-50" borderClass="border-green-200" />
          <RulesList icon={Zap} title="Reaction Latency Rules" items={human_motion_profile.reaction_latency_rules}
            colorClass="text-orange-700" bgClass="bg-orange-50" borderClass="border-orange-200" />
          <RulesList icon={Eye} title="Attention Allocation Rules" items={human_motion_profile.attention_allocation_rules}
            colorClass="text-indigo-700" bgClass="bg-indigo-50" borderClass="border-indigo-200" />
          <RulesList icon={Activity} title="Performance Intensity Limits" items={human_motion_profile.performance_intensity_limits}
            colorClass="text-red-700" bgClass="bg-red-50" borderClass="border-red-200" />
          <SpeechBehaviorCard sbp={human_motion_profile.speech_behavior_profile || {}} />
          <RulesList icon={Hand} title="Gesture Rules" items={gesture_rules}
            colorClass="text-amber-700" bgClass="bg-amber-50" borderClass="border-amber-200" />
          <RulesList icon={Zap} title="Instability Rules" items={instability_rules}
            colorClass="text-yellow-700" bgClass="bg-yellow-50" borderClass="border-yellow-200" />
          <RulesList icon={Smartphone} title="Smartphone Motion Rules" items={smartphone_motion_rules}
            colorClass="text-gray-700" bgClass="bg-gray-50" borderClass="border-gray-200" />
        </div>

        <ReviewGate
          execution={execution}
          onApprove={onApprove}
          onReject={onReject}
          onRegenerate={onRegenerate}
          loading={loading}
        />

      </div>
    </div>
  );
}
